import { toRef, type MaybeRef } from 'vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { geofencingService } from '@/services/geofencing'
import type { IGeofenceCircleData, IGeofencePolygonData } from '@/models/geofencing'
import { useSnackbarStore } from '@/stores/snackbar'

export function useGeofences(siteCode: MaybeRef<number | undefined>) {
  const siteCodeRef = toRef(siteCode)
  const queryKey = ['geofences', siteCodeRef]

  const { data: geofences, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () => geofencingService.fetchGeofences(siteCodeRef.value!),
    enabled: () => typeof siteCodeRef.value === 'number'
  })

  return { geofences, isLoading, error, queryKey, refetch }
}

export function useCreateGeofence(siteCode: MaybeRef<number>) {
  const siteCodeRef = toRef(siteCode)
  const queryClient = useQueryClient()
  const snackbarStore = useSnackbarStore()

  const { isPending, mutateAsync, reset } = useMutation({
    mutationKey: ['create-geofence'],
    mutationFn: (payload: IGeofenceCircleData | IGeofencePolygonData) =>
      geofencingService.createGeofence(siteCodeRef.value, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['geofences', siteCodeRef.value] })
      snackbarStore.showSnackbar('Geofence created successfully')
    },
    onError: () => {
      snackbarStore.showSnackbar('Failed to create geofence', 'error')
    }
  })

  return { isPending, createGeofence: mutateAsync, reset }
}

export function useUpdateGeofence(siteCode: MaybeRef<number>) {
  const siteCodeRef = toRef(siteCode)
  const queryClient = useQueryClient()
  const snackbarStore = useSnackbarStore()

  const { isPending, mutateAsync, reset } = useMutation({
    mutationKey: ['update-geofence'],
    mutationFn: ({ id, payload }: { id: number; payload: IGeofenceCircleData | IGeofencePolygonData }) =>
      geofencingService.updateGeofence(siteCodeRef.value, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['geofences', siteCodeRef.value] })
      snackbarStore.showSnackbar('Geofence updated successfully')
    },
    onError: () => {
      snackbarStore.showSnackbar('Failed to update geofence', 'error')
    }
  })

  return { isPending, updateGeofence: mutateAsync, reset }
}

export function useDeleteGeofence(siteCode: MaybeRef<number>) {
  const siteCodeRef = toRef(siteCode)
  const queryClient = useQueryClient()
  const snackbarStore = useSnackbarStore()

  const { isPending, mutateAsync, reset } = useMutation({
    mutationKey: ['delete-geofence'],
    mutationFn: (id: number) => geofencingService.deleteGeofence(siteCodeRef.value, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['geofences', siteCodeRef.value] })
      snackbarStore.showSnackbar('Geofence deleted successfully')
    },
    onError: () => {
      snackbarStore.showSnackbar('Failed to delete geofence', 'error')
    }
  })

  return { isPending, deleteGeofence: mutateAsync, reset }
}
